import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import moment from 'moment';
import { LiveMessenger } from 'react-aria-live';
import helpers from '../../helpers';
import reactT from "../reactT";

const langcode = document.querySelector('html').getAttribute('lang');
moment.locale(langcode);
moment.updateLocale(langcode, {
  longDateFormat: {
      L: "DD/MM/YYYY",
  }
});

// Create dumb component which can be configured by props.
const FacetType = props => (
  <button className="applied-filters__filter is-active" key={props.id} onClick={props.onClick}>
    <span className="element-invisible">
      Remove filter
    </span>
    <span className="facet-item__status js-facet-deactivate">
    </span>
    {props.children}
  </button>
);

// Configure and render the FacetType component to render as list facet type.
class ListFacetType extends React.Component {
  removeListFacetValue(field, values, value) {
    this.props.announcePolite(`Removed ${field.value} filter.`);

    // Staged modal mode (UNHCRRW-2406): only toggle the pending selection; the
    // querystring and search are not touched until the user clicks "Apply".
    if (this.props.onStage) {
      this.props.onStage(field, value);
      return;
    }

    const {
      foundIdx,
      parsed,
      isQsParamField,
      param,
    } = helpers.qs.getFieldQsInfo({
      field,
      values,
      value,
    });

    // Confirm the field value is set in state.
    if (foundIdx > -1) {
      // If the field is one whose state is tracked in qs and there is currently a param for it.
      if (isQsParamField && param) {
        const newParsed = helpers.qs.removeValueFromQsParam({
          field,
          value,
          param,
          parsed,
        });

        helpers.qs.addNewUrlToBrowserHistory(newParsed);
      }

      // Send query based on new state.
      this.props.onChange(field, values.filter((v, i) => i !== foundIdx));
    }
  }

  render() {
    const { searchField } = this.props;
    let labels = [];

    if (typeof drupalSettings !== 'undefined' && searchField.field === 'ss_type') {
      labels = drupalSettings.content_types_labels;
      /*global drupalSettings:true*/
        return (searchField.value.map((val, i) => (
        <FacetType
          key={i}
          id={i}
          onClick={() => this.removeListFacetValue(searchField.field, searchField.value, val)}
        >
          {/* Add spacing to hierarchical facet values: Type>Term = Type > Term. */}
          {labels[val].label}
        </FacetType>
      )));
    }
    else {
      if (searchField.field === 'sm_country_classification_name') {
        searchField.value = reactT(searchField.value);
      }

      return (searchField.value.map((val, i) => (
      <FacetType
        key={i}
        id={i}
        onClick={() => this.removeListFacetValue(searchField.field, searchField.value, val)}
      >
        {/* Add spacing to hierarchical facet values: Type>Term = Type > Term. */}
        {val.replace('>', ' > ')}
      </FacetType>
    )));
    }
  }
}

// Configure and render the FacetType component to render as range facet type.
class RangeFacetType extends React.Component {
  removeRangeFacetValue(field) {
    this.props.announcePolite(`Removed ${field.field.value} filter.`);
    this.props.onChange(field.field, []);

    // Find the reset button.
    const resetDateButton = document.querySelector('.reset-date-range');
    if (resetDateButton) {
      resetDateButton.click();
    }
  }

  render() {
    const { searchField } = this.props;
    // Create a moment from the search start date.
    const start = moment(searchField.value[0]);
    // Use UTC.
    start.utc();
    // Create a formatted string from start date.
    const startFormatted = start.format("DD/MM/YYYY");
    // Create a moment from search end date.
    const end = moment(searchField.value[1]);
    // Use utc.
    end.utc();
    // Create a formatted string from end date.
    const endFormatted = end.format("DD/MM/YYYY");
    // Determine if we chose the same or different start / end dates.
    const diff = start.diff(end, 'days');
    // Only show the start date if the same date were chosen, otherwise: start - end.
    const filterValue = diff ? `${startFormatted} - ${endFormatted}` : startFormatted;
    return (
      <FacetType onClick={() => this.removeRangeFacetValue(searchField)}>
        {filterValue}
      </FacetType>
    );
  }
}

// Configure and render the FacetType component to render as text facet type.
class TextFacetType extends React.Component {
  removeTextValue(field) {
    this.props.announcePolite(`Removed search term ${field.value}.`);
    this.props.onChange(field, '');
    // Get current querystring params.
    const parsed = queryString.parse(window.location.search);
    // Remove the search term param, if it exists.
    if (parsed.keywords) {
      delete parsed.keywords;
    }
    const stringified = queryString.stringify(parsed);
    // Update the querystring params in the browser, add path to history.
    // See: https://developer.mozilla.org/en-US/docs/Web/API/History_API#The_pushState()_method
    if (window.history.pushState) {
      if (stringified) {
        const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?${stringified}`;
        window.history.pushState({ path: newurl }, '', newurl);
      } else {
        const newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
        window.history.pushState({ path: newurl }, '', newurl);
      }
    } else {
      window.location.search = stringified;
    }
  }

  render() {
    const { searchField } = this.props;
    return (
      <FacetType onClick={() => this.removeTextValue(searchField.field)}>
        {searchField.value}
      </FacetType>
    );
  }
}

class FederatedCurrentQuery extends React.Component {
  render() {
    const {
      query, currentQueryType, currentQueryField, overrideValue,
    } = this.props;
    let fields = query.searchFields.filter(
      (searchField) => searchField.value && searchField.value.length > 0
    );

    // Staged modal mode (UNHCRRW-2406): render the chips for the targeted field
    // from the pending (override) value instead of the live applied state.
    if (overrideValue !== undefined && currentQueryField !== undefined) {
      fields = query.searchFields
        .filter((searchField) => searchField.field === currentQueryField)
        .map((searchField) => ({ ...searchField, value: overrideValue }))
        .filter((searchField) => searchField.value && searchField.value.length > 0);
    }

    // Create a map of known facet type child components which can be rendered dynamically.
    const facetTypes = {
      "list-facet": ListFacetType,
      "range-facet": RangeFacetType,
      text: TextFacetType,
    };

    // The reset-link visibility toggle is a global side effect; skip it in the
    // staged modal mode so it doesn't react to a single field's pending value.
    if (overrideValue === undefined) {
      if (fields.length > 1) {
        const element = document.getElementById("fs-root");
        if (element !== null) {
          element.classList.remove("hide-reset");
        }
      } else {
        const element = document.getElementById("fs-root");
        if (element !== null) {
          element.classList.add("hide-reset");
        }
      }
    }

    return (
      <LiveMessenger>
        {({ announcePolite }) => (
          <React.Fragment>
            {fields.length > 0 && ( // Only render this if there are filters applied.
              <div
                className={
                  "applied-filters block-facet-filters-block" +
                  (this.props.className !== undefined
                    ? " " + this.props.className
                    : "")
                }
              >
                {/* Only render the values for visible facets / filters */}
                {fields
                  .filter(
                    (searchField) =>
                      !searchField.isHidden &&
                      (currentQueryType === undefined
                        ? true
                        : searchField.type === currentQueryType &&
                          currentQueryField === searchField.field)
                  )
                  .map((searchField, i) => {
                    // Determine which child component to render.
                    const MyFacetType = facetTypes[searchField.type];
                    return (
                      <MyFacetType
                        {...this.props}
                        key={i}
                        searchField={searchField}
                        announcePolite={announcePolite}
                      />
                    );
                  })}
              {fields
              .filter((searchField) =>
                currentQueryType === undefined &&
                currentQueryField === undefined
                  ? false
                  : searchField.type === currentQueryType &&
                    currentQueryField === searchField.field
              )
              .map((searchField, i) => {
                return (
                  <a
                    className="clear"
                    key={i}
                    href="!#"
                    onClick={this.props.onClear}
                  >
                    x {reactT("Clear selection")}
                  </a>
                );
              })}
              </div>
            )}
          </React.Fragment>
        )}
      </LiveMessenger>
    );
  }
}

FederatedCurrentQuery.propTypes = {
  onChange: PropTypes.func,
  query: PropTypes.object,
};

export default FederatedCurrentQuery;
